import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	effect,
	ElementRef,
	inject,
	Inject,
	OnDestroy,
	OnInit,
	signal,
	viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, distinctUntilChanged, forkJoin, map, Observable, of } from 'rxjs';

import {
	ALARIS_PROPERTY_MATCHER,
	AlarisAlertComponent,
	AlarisButtonComponent,
	AlarisCheckboxComponent,
	AlarisComplexTooltipDirective,
	AlarisCostComponent,
	AlarisCustomEvent,
	AlarisDialogService,
	AlarisEditPanelService,
	AlarisFadeDirective,
	AlarisFileLoaderComponent,
	AlarisFilesService,
	AlarisFilterPipe,
	AlarisFocusFirstInvalidInputDirective,
	AlarisIconDirective,
	AlarisInputComponent,
	AlarisLinkComponent,
	AlarisLoadingDirective,
	AlarisOptions2Component,
	AlarisParsedFileTableComponent,
	AlarisProfileService,
	AlarisRadioComponent,
	AlarisScheduledComponent,
	AlarisSelect2Component,
	AlarisSelectFilter2Component,
	AlarisTooltipDirective,
	EditPanelWidth,
	InputError,
	PROFILE_SERVICE_INJECTOR,
} from '@campaign-portal/components-library';

import {
	DecisionMode,
	MessagePurpose,
	SenderSubscriptionEnabledStatus,
	TrafficType,
} from '@campaign-portal/namespace/common/enums';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { ApproximateData } from '@campaign-portal/namespace/entities/campaigns/specs';
import { SenderSubscription } from '@campaign-portal/namespace/entities/sender-id/specs';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { FileRow, FilesReports } from '@campaign-portal/namespace/entities/files/specs';

import { ViberPriorityComponent } from '@helpers/components/viber-priority/viber-priority.component';
import { ChannelsService } from '@helpers/services/channels.service';
import { CampaignWizardService } from '../campaign-wizard/campaign-wizard.service';
import { SendersService } from '../services/senders.service';
import { RequestSenderInputData, SenderRequestComponent } from '../senders/sender-request/sender-request.component';
import {
	CampaignChannelSettingsControls,
	CampaignOmniSettingsControls,
	CampaignViberSettingsControls,
} from '../shared/types/form-types';
import { buttonActionUrl, buttonCaption, imageUrl, imMessage, message, phone } from './fields';
import { CampaignsDialogComponent } from '../shared/dialog/campaigns-dialog.component';
import { CampaignsService } from '../services/campaigns.service';
import { PurchaseService } from '../../finance/purchase/purchase.service';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyCampaignCardComponent } from '../shared/empty-campaign-card/empty-campaign-card.component';
import { TuiLegendItem, TuiRingChart } from '@taiga-ui/addon-charts';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { MessageTemplateUtilsService } from '../message-templates/templates/update-template/services/message-template-utils.service';
import { CampaignUtilsService } from '../services/campaign-utils.service';

@Component({
	selector: 'app-campaign-from-file',
	templateUrl: './campaign-from-file.component.html',
	styleUrls: ['./campaign-from-file.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlarisLoadingDirective,
		AlarisIconDirective,
		TranslateModule,
		EmptyCampaignCardComponent,
		AlarisFadeDirective,
		AlarisInputComponent,
		AlarisSelect2Component,
		AlarisOptions2Component,
		AlarisSelectFilter2Component,
		AlarisLinkComponent,
		AlarisButtonComponent,
		TuiLegendItem,
		TuiRingChart,
		AlarisScheduledComponent,
		AlarisRadioComponent,
		AlarisComplexTooltipDirective,
		AsyncPipe,
		AlarisParsedFileTableComponent,
		AlarisFileLoaderComponent,
		ReactiveFormsModule,
		AlarisFilterPipe,
		AlarisCheckboxComponent,
		AlarisTooltipDirective,
		AlarisCostComponent,
		DecimalPipe,
		AlarisAlertComponent,
	],
	hostDirectives: [AlarisFocusFirstInvalidInputDirective],
	standalone: true,
})
export class CampaignFromFileComponent implements OnInit, OnDestroy {
	readonly statisticsEl = viewChild<ElementRef>('statistics');
	readonly TrafficType = TrafficType;
	readonly MessagePurpose = MessagePurpose;
	readonly tooltipPriorityComponent = ViberPriorityComponent;

	isFileLoaderRequired = signal(false);

	hasSubscriptions = false;
	hasSenders = false;

	fileRows: FileRow[] = [];
	totalRecipients = 0;

	senders: SenderSubscription<exist>[] = [];
	readonly filterSenderIdsControl = new FormControl('');
	readonly sendersMatcher = ALARIS_PROPERTY_MATCHER<SenderSubscription<exist>>('name');
	readonly scheduledChoiceControl = new FormControl<'now' | 'future'>('now', { nonNullable: true });

	approximateCampaignData: ApproximateData | null = null;
	approximateFileAnalysis: FilesReports | null = null;

	activeItemIndex = NaN;

	readonly errors: InputError[] = [{ key: 'required', value: 'errors.required' }];

	readonly fileLoading$ = new BehaviorSubject<boolean>(false);
	readonly analysis$ = new BehaviorSubject<boolean>(false);
	fields = [phone, message];

	loading$ = new Observable<boolean>();
	private readonly destroyedRef = inject(DestroyRef);

	constructor(
		@Inject(PROFILE_SERVICE_INJECTOR) public readonly profileService: AlarisProfileService,
		public readonly cw: CampaignWizardService,
		public readonly sendersService: SendersService,
		public readonly channelsService: ChannelsService,
		public readonly templateUtils: MessageTemplateUtilsService,
		public readonly campaignUtils: CampaignUtilsService,
		private readonly campaignsService: CampaignsService,
		private readonly filesService: AlarisFilesService,
		private readonly purchaseService: PurchaseService,
		private readonly editPanel: AlarisEditPanelService,
		private readonly dialog: AlarisDialogService,
		private readonly cd: ChangeDetectorRef,
		private readonly router: Router
	) {
		effect(() => {
			this.statisticsEl()?.nativeElement.scrollIntoView({ behavior: 'smooth' });
		});
	}

	get isOnlyPreModeratedTemplateChannel(): boolean {
		return (
			this.channelControls.trafficType.value &&
			this.templateUtils.isPreModeratedChannel(this.channelControls.trafficType.value) &&
			!this.isPreModeratedCampaignChannel
		);
	}

	get isPreModeratedCampaignChannel(): boolean {
		return (
			this.channelControls.trafficType.value &&
			this.campaignUtils.isPreModeratedChannel(this.channelControls.trafficType.value)
		);
	}

	get channelControls(): CampaignChannelSettingsControls {
		return this.cw.setupControls.channels.at(0).controls;
	}

	get templateRef(): string {
		switch (this.channelControls.trafficType.value) {
			case TrafficType.SMS:
				return 'assets/templates/campaign_template_sms.xlsx';
			case TrafficType.VIBER:
				return 'assets/templates/campaign_template_viber.xlsx';
			default:
				return 'assets/templates/campaign_template.xlsx';
		}
	}

	get createCampaignDisabled(): boolean {
		const { form, setupControls } = this.cw;
		const columns = setupControls.file.controls.columns.value ?? [];
		const trafficType = this.channelControls.trafficType.value;

		const invalid = form.invalid;
		const noPhone = !columns.some((order) => order.columnName === phone.variable);
		const noMessage = !columns.some(
			(order) => order.columnName === (trafficType === TrafficType.SMS ? message.variable : imMessage.variable)
		);
		const noCorrectRow = this.approximateFileAnalysis !== null && !this.approximateFileAnalysis?.correctRow;
		const noRecipients = this.approximateCampaignData !== null && !this.approximateCampaignData?.recipients;

		return invalid || noPhone || noMessage || noCorrectRow || noRecipients;
	}

	ngOnInit(): void {
		this.initializeCampaign();
		this.handleTrafficTypeChanges();
		this.handleScheduledChoiceChanges();
		this.subscribeToPurchaseService();
		this.setupLoadingStream();
		this.handleFileChanges();

		this.cw.settingsControls.isFlashed.valueChanges
			.pipe(takeUntilDestroyed(this.destroyedRef))
			.subscribe((isFlashed) => {
				if (isFlashed) {
					this.cw.settingsControls.isMo.setValue(false);
				}
			});
	}

	ngOnDestroy(): void {
		this.fileLoading$.complete();
		this.analysis$?.complete();
		this.cw.reset();
	}

	openSenderIdRequestPanel(): void {
		this.editPanel.close();
		this.editPanel.open<SenderRequestComponent, RequestSenderInputData, void>(
			SenderRequestComponent,
			EditPanelWidth.SM,
			{ requestCustomFields: true }
		);
	}

	displaySender(id: Id<exist>): string {
		return this.sendersService.map.get(id)?.name ?? '';
	}

	addFiles($event: (FileInfo | undefined)[]): void {
		const file = $event[0];
		const fileControls = this.cw.setupControls.file.controls;

		if (file) {
			this.fileLoading$.next(true);
			fileControls.file.setValue(file);

			this.filesService
				.parse(file.id)
				.pipe(takeUntilDestroyed(this.destroyedRef))
				.subscribe(({ Data, Total }) => {
					this.fileRows = Data;
					this.totalRecipients = Total;
					this.fileLoading$.next(false);
				});
		} else {
			this.clearFileData();
		}
	}

	toReplyTemplates(): void {
		this.editPanel.close();
		void this.router.navigate(['campaigns', 'templates'], { queryParams: { templatesTabs: 'replyTemplates' } });
	}

	createCampaign(event: MouseEvent): void {
		if (this.createCampaignDisabled) {
			const focusFirstInvalidInput = new Event(AlarisCustomEvent.FOCUS_FIRST_INVALID_INPUT, { bubbles: true });
			event.target?.dispatchEvent(focusFirstInvalidInput);
			this.isFileLoaderRequired.set(true);
			return;
		}

		if (this.approximateCampaignData) {
			this.cw.form.controls.statistics.enable();
			this.cw.statisticsControls.preCalculatedCost.setValue(this.approximateCampaignData.cost);
		} else {
			this.cw.form.controls.statistics.disable();
		}

		this.dialog
			.open(CampaignsDialogComponent, {
				data: {
					campaign: this.cw.prepare('file'),
					type: 'Confirm',
					total: this.approximateCampaignData?.recipients ?? this.totalRecipients,
				},
				autoFocus: false,
			})
			.closed.pipe(takeUntilDestroyed(this.destroyedRef))
			.subscribe((v) => {
				if (v) {
					this.editPanel.close();
				}
			});
	}

	campaignDetails(): void {
		this.analysis$.next(true);

		const fileId = this.cw.setupControls.file.controls.file.value?.id;
		const columns = this.cw.setupControls.file.controls.columns.value;

		if (!fileId || !columns) {
			this.analysis$.next(false);
			return;
		}

		const campaign$ = this.campaignsService.approximate(this.cw.prepare('file')).pipe(
			catchError(() => {
				return of({ Success: false, Data: null });
			})
		);

		const analysis$ = this.filesService
			.analysis({
				Data: { fileId, columnName: phone.variable, format: 'number', columns },
			})
			.pipe(
				catchError(() => {
					return of({ Success: false, Data: null });
				})
			);

		forkJoin([campaign$, analysis$])
			.pipe(takeUntilDestroyed(this.destroyedRef))
			.subscribe(([campaignData, fileAnalysis]) => {
				this.approximateCampaignData = campaignData.Success ? campaignData.Data : null;
				this.approximateFileAnalysis = fileAnalysis.Success ? fileAnalysis.Data : null;
				this.analysis$.next(false);
			});
	}

	moveToPurchase(): void {
		this.editPanel.close();
		this.router.navigate(['/purchase']);
	}

	makeNaN(): void {
		this.activeItemIndex = NaN;
	}

	isItemActive(index: number): boolean {
		return this.activeItemIndex === index;
	}

	onHover(index: number, hovered: MouseEvent): void {
		this.activeItemIndex = hovered ? index : 0;
	}

	private clearFileData(): void {
		const fileControls = this.cw.setupControls.file.controls;

		this.totalRecipients = 0;
		this.fileRows = [];
		fileControls.file.setValue(null);
		fileControls.columns.setValue([]);
		this.resetAnalysis();
	}

	private handleFileChanges(): void {
		this.cw.setupControls.file.valueChanges
			.pipe(takeUntilDestroyed(this.destroyedRef))
			.subscribe(() => this.resetAnalysis());
	}

	private updateLoadingState(ssLoading: boolean, psLoading: boolean): boolean {
		if (!ssLoading) {
			this.hasSenders = this.sendersService.active$.getValue().length > 0;
		}
		this.cd.detectChanges();
		return ssLoading || psLoading;
	}

	private setupLoadingStream(): void {
		this.loading$ = combineLatest([this.sendersService.loading$, this.purchaseService.loadingSubscribed$]).pipe(
			map(([ssLoading, psLoading]: boolean[]) => this.updateLoadingState(ssLoading, psLoading)),
			distinctUntilChanged(),
			takeUntilDestroyed(this.destroyedRef)
		);
	}

	private subscribeToPurchaseService(): void {
		this.purchaseService
			.subscribed()
			.pipe(takeUntilDestroyed(this.destroyedRef))
			.subscribe((subscribed) => {
				this.hasSubscriptions = !!(subscribed.Data.packRecords.size || subscribed.Data.plans.length);
				this.cd.markForCheck();
			});
	}

	private addOmniControl(): void {
		this.cw.setupControls.channels.at(0).addControl(
			'omni',
			new FormGroup<CampaignOmniSettingsControls>({
				fallbackType: new FormControl<DecisionMode>(DecisionMode.BY_SUBMITTED, { nonNullable: true }),
				viberSettings: new FormGroup<CampaignViberSettingsControls>({
					buttonActionUrl: new FormControl<string>('', { nonNullable: true }),
					imageUrl: new FormControl<string>('', { nonNullable: true }),
					buttonCaption: new FormControl<string>('', { nonNullable: true }),
					messagePurpose: new FormControl<MessagePurpose>(MessagePurpose.TRANSACTION, {
						nonNullable: true,
						validators: Validators.required,
					}),
				}),
			})
		);
	}

	private removeOmniControl(): void {
		if (this.channelControls.omni) {
			this.cw.setupControls.channels.at(0).removeControl('omni');
		}
	}

	private updateChannelSettings(value: TrafficType): void {
		if (value === TrafficType.SMS) {
			this.removeOmniControl();
			this.fields = [phone, message];
		} else if (value === TrafficType.VIBER) {
			this.fields = [phone, imMessage, buttonActionUrl, imageUrl, buttonCaption];
			this.addOmniControl();
		} else {
			this.fields = [phone, message, imMessage, buttonActionUrl, imageUrl, buttonCaption];
		}
		this.prepareSenders(value);
		this.channelControls.sender.reset();
	}

	private handleTrafficTypeChanges(): void {
		this.channelControls.trafficType.valueChanges
			.pipe(takeUntilDestroyed(this.destroyedRef))
			.subscribe((value) => this.updateChannelSettings(value));
	}

	private handleScheduledChoiceChanges(): void {
		this.scheduledChoiceControl.valueChanges.pipe(takeUntilDestroyed(this.destroyedRef)).subscribe(() => {
			this.cw.infoControls.scheduled.setValue(new Date().toISOString());
		});
	}

	private addDefaultChannel(): void {
		this.cw.setupControls.channels.push(
			new FormGroup<CampaignChannelSettingsControls>({
				sender: new FormControl<Id<exist> | null>(null, { validators: Validators.required }),
				trafficType: new FormControl<TrafficType>(TrafficType.SMS, {
					nonNullable: true,
					validators: Validators.required,
				}),
			})
		);
	}

	private initializeCampaign(): void {
		this.cw.create('file');
		this.addDefaultChannel();
		this.prepareSenders(TrafficType.SMS);
	}

	private resetAnalysis(): void {
		this.approximateCampaignData = null;
		this.approximateFileAnalysis = null;
	}

	private prepareSenders(value: TrafficType): void {
		this.filterSenderIdsControl.reset();
		this.senders = this.sendersService.list.filter(
			(sender) => sender.trafficType === value && sender.enabled === SenderSubscriptionEnabledStatus.ENABLED
		);
	}
}
